import { useCallback, useRef, useState } from 'react';
import { type To, useLocation, useNavigate } from 'react-router-dom';

import { SearchAsYouTypeResult } from '@/core/graphql/generated/types';
import { paths } from '@/core/router/paths';
import { hcoUrl } from '@/utils/hco/hcoUrl';
import { isOfTypeHco } from '@/utils/hco/isOfTypeHco';
import { hcpUrl } from '@/utils/hcp/hcpUrl';
import { scrollBackToTop } from '@/utils/scrollBackToTop';
import { replaceRouteParams } from '@/utils/url/replaceRouteParams';

import { isOfTypeHcp } from '../../../utils/hcp/isOfTypeHcp';
import { useGetQuickSearchResultsLazyQuery } from '../_graphql/query/GetQuickSearchResults.hook';
import {
	isProfileVisit,
	isQuickSearchResult,
	isRecentSearch,
	QuickSearchOption
} from '../_types/QuickSearch.types';

export const useQuickSearch = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const [options, setOptions] = useState<SearchAsYouTypeResult[] | null>(null);
	const [resultsQuery, setResultsQuery] = useState('');
	const latestQueryRef = useRef('');

	const [fetchQuickSearchResults, { loading }] =
		useGetQuickSearchResultsLazyQuery({
			fetchPolicy: 'cache-first'
		});

	const updateResults = useCallback(
		async (query: string) => {
			latestQueryRef.current = query;

			const res = await fetchQuickSearchResults({
				variables: {
					searchPayload: {
						searchString: query
					}
				}
			});

			if (
				latestQueryRef.current === query &&
				res.data?.searchAsYouType?.__typename === 'SearchAsYouTypeResults'
			) {
				setOptions(res.data.searchAsYouType.results as SearchAsYouTypeResult[]);
				setResultsQuery(query);
			}
		},
		[fetchQuickSearchResults]
	);

	const resetResults = useCallback(() => {
		latestQueryRef.current = '';
		setOptions(null);
		setResultsQuery('');
	}, []);

	const handleSelection = useCallback(
		(newValue: QuickSearchOption | string | null) => {
			if (!newValue || typeof newValue === 'string') return;
			let to: To | null = null;

			if (isProfileVisit(newValue)) {
				const { profile } = newValue;

				if (pathname.includes(profile.vid_V)) {
					scrollBackToTop();
					return;
				}

				if (isOfTypeHcp(profile)) {
					to = replaceRouteParams(paths.hcp, {
						hcpId: hcpUrl({
							firstName: profile.firstNameCda_V,
							lastName: profile.lastNameCda_V,
							id: profile.vid_V
						})
					});
				} else if (isOfTypeHco(profile)) {
					to = replaceRouteParams(paths.hco, {
						hcoId: hcoUrl({ name: profile.hcoNameCda_V, id: profile.vid_V })
					});
				}
			} else if (isRecentSearch(newValue)) {
				const currentSearch = new URLSearchParams(search);
				currentSearch.delete('page');
				const newSearch = new URLSearchParams(newValue.queryString);

				if (currentSearch.toString() === newSearch.toString()) {
					scrollBackToTop();
					return;
				}

				to = {
					pathname:
						newValue.payload.__typename === 'HCPSearchAndFiltersType'
							? paths.searchHcp
							: paths.searchHco,
					search: newValue.queryString
				};
			} else if (isQuickSearchResult(newValue)) {
				const profile = newValue.entity;

				if (pathname.includes(profile.vid_V)) {
					scrollBackToTop();
					return;
				}

				if (isOfTypeHcp(profile)) {
					to = replaceRouteParams(paths.hcp, {
						hcpId: hcpUrl({
							firstName: profile.firstNameCda_V,
							lastName: profile.lastNameCda_V,
							id: profile.vid_V
						})
					});
				} else if (isOfTypeHco(profile)) {
					to = replaceRouteParams(paths.hco, {
						hcoId: hcoUrl({ name: profile.hcoNameCda_V, id: profile.vid_V })
					});
				}
			}

			if (to) {
				navigate(to);
			}
		},
		[navigate, pathname, search]
	);

	return {
		loading,
		options,
		resultsQuery,
		handleSelection,
		updateResults,
		resetResults
	};
};
